import React, { useEffect, useMemo, useState }              from 'react';
import {
  CheckboxVisibility,
  DefaultButton,
  Dropdown, IColumn,
  IconButton,
  IDropdownOption,
  Modal,
  ShimmeredDetailsList,
  Stack,
  TooltipHost,
  SelectionMode,
}                                                           from 'office-ui-fabric-react';
import PrimaryButton                                        from 'views/components/UI/PrimaryButton';
import { theme }                                            from "theme";
import styled                                               from 'styled-components';
import { deleteListRA, getList, saveElement }               from 'api/handbooks/table';
import { checkCreate, checkDelete, checkRead, checkUpdate } from 'utils/permissions';
import history                                              from 'utils/history';
import { apiTypes }                                         from 'shared/consts/handbooksApiTypes';
import devtempsensConfig                                    from 'views/pages/Handbooks/SpreadSheetHandbook/configs/devtempsens';
import devtempsenstypesConfig                               from 'views/pages/Handbooks/SpreadSheetHandbook/configs/devtempsenstypes';
import { cloneDeep }                                        from 'lodash';


interface TemperatureSensorType {
  id: number
  sensor_name: string
  sensor_type: string
  eqn: string
}

interface TemperatureSensor {
  id: number | null
  sensor_type_id: number | null
  user_serial_number: number | null
  vehicle_id: number
}


interface IProps {
  vehicle_id: number
  temperatureSensorTypes: Array<TemperatureSensorType>
  parentConfig: any
}

interface IItem extends TemperatureSensor {
  __oldValue: TemperatureSensor | null
  __errors: string[]
}


const ModalWrapper = styled.div`
  width: 600px;
  max-height: 90vh;
  overflow-x: hidden;
`;

const HeaderWrapper = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.colors.blue};
  color: ${({ theme }) => theme.colors.white};
`;

const DetailsListWrapper = styled.div`
  padding: 10px 30px;
`;

const HeaderTitle = styled.h3`
  flex-grow: 1;
  text-align: center;
  margin-top: 0;
  margin-bottom: 0;
  vertical-align: center;
`

const columns: Array<IColumn> = [
  {
    name:      'Номер',
    key:       'user_serial_number',
    fieldName: 'user_serial_number',
    minWidth:  40,
    maxWidth:  80,
  },
  {
    name:      'Тип датчика',
    key:       'sensor_type_id',
    fieldName: 'sensor_type_id',
    minWidth:  150,
    maxWidth:  500,
  },
  {
    name:      '',
    key:       'deleteButton',
    fieldName: 'deleteButton',
    minWidth:  40,
    maxWidth:  100,
  },
]

const INITIAL_USER_SERIAL_NUMBER_OPTIONS =
        [...Array(8)].map((_, index) => index + 1);

const SensorSelection = ({ vehicle_id, parentConfig, temperatureSensorTypes }: IProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
  const isFullCRUD = checkCreate(history.location.pathname) &&
                     checkRead(history.location.pathname) &&
                     checkUpdate(history.location.pathname) &&
                     checkDelete(history.location.pathname);


  const [itemsMap, setItemsMap] = useState<Map<number, IItem>>(new Map());
  const [rowsToDelete, setRowsToDelete] = useState<Array<IItem>>([]);
  const [rowsToUpdateOrCreate, setRowsToUpdateOrCreate] = useState<Map<number, IItem>>(new Map());
  const [userSerialNumberDropdownOptions, setUserSerialNumberDropdownOptions] = useState<IDropdownOption[]>([]);
  const [sensorTypeDropdownOptions, setSensorTypeDropdownOptions] = useState<IDropdownOption[]>([]);
  const [validTemperatureSensorTypes, setValidTemperatureSensorTypes] = useState<TemperatureSensorType[]>([]);

  useEffect(() => {
    setValidTemperatureSensorTypes(temperatureSensorTypes.filter((sensor) => sensor &&
                                                                             sensor.sensor_type &&
                                                                             sensor.sensor_type.trim() !== '' &&
                                                                             sensor.sensor_name &&
                                                                             sensor.sensor_name.trim() !== ''))
  }, [temperatureSensorTypes]);


  const calculateUserSerialNumberDropDownOptions = (data: Map<number, IItem>): IDropdownOption[] => {
    const selectedValues = Array.from(data.values()).map((item) => item['user_serial_number']);

    return [...INITIAL_USER_SERIAL_NUMBER_OPTIONS.map((number) => ({
      key: number,
      text: `${number}`,
      hidden: selectedValues.includes(number),
      disabled: selectedValues.includes(number),
    })), { key: null as unknown as number, text: '' }];
  };

  const calculateSensorTypeDropDownOptions = (data: Map<number, IItem>): IDropdownOption[] => {
    if (!validTemperatureSensorTypes.length) {
      return [
        {
          key:      'nodata',
          text:     'Нет данных',
          disabled: true,
        },
      ];
    }

    const selectedValues = Array.from(data.values()).map((item) => item['sensor_type_id']);

    return [...validTemperatureSensorTypes.map((type) => ({
      key: type.id,
      text: type.sensor_name,
      hidden: selectedValues.includes(type.id),
      disabled: selectedValues.includes(type.id),
    })), { key: '', text: '' }];
  };


  useEffect(() => {
    async function getTemperatureSensorsData() {
      try {
        setIsRequestPending(true);
        const res = await getList({
                                    table:   devtempsensConfig.table,
                                    fields:  devtempsensConfig.fields.map((field) => field.name),
                                    filters: [{ vehicle_id: [vehicle_id?.toString()] }],
                                    apiType: apiTypes.REPORT_API,
                                    sort:    { field: 'user_serial_number', direction: 'asc' },
                                    type:    'table',
                                  }) as { count: number; list: Array<TemperatureSensor> }
        if (!res?.list) {
          return;
        }
        const newMap = new Map();
        res.list.map((item, index) => newMap.set(
          index,
          {
            ...item,
            __oldValue: cloneDeep(item),
            __errors:   [],
          },
        ));
        setItemsMap(newMap);
        setUserSerialNumberDropdownOptions(calculateUserSerialNumberDropDownOptions(newMap));
        setSensorTypeDropdownOptions(calculateSensorTypeDropDownOptions(newMap));
      } catch (error) {
        console.log(error);
      } finally {
        setIsRequestPending(false)
      }
    }

    if (isOpen) {
      getTemperatureSensorsData();
    }
  }, [isOpen]);

  const updateTemperatureSensors = async () => {
    try {
      setIsRequestPending(true);

      if (rowsToDelete.length) {
        deleteListRA(devtempsensConfig, rowsToDelete);
      }

      if (rowsToUpdateOrCreate.size) {
        saveElement(devtempsensConfig, Array.from(rowsToUpdateOrCreate.values()));
      }

      setIsOpen(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsRequestPending(false);
    }
  }

  const items = useMemo(() => [...Array.from(itemsMap.values())], [itemsMap, sensorTypeDropdownOptions, userSerialNumberDropdownOptions]);

  const fillInSensorsByDefault = async () => {
    const config = {
      table:   devtempsenstypesConfig.table,
      fields:  [],
      sort:    devtempsenstypesConfig.defaultSort,
      limit:   devtempsenstypesConfig.elemsOnPage,
      offset:  0,
      filters: [],
      apiType: devtempsenstypesConfig.apiType,
      type:    'list',
    }

    try {
      setIsRequestPending(true)
      const res = await getList(config) as { count: number; list: Array<TemperatureSensorType> };

      const map = new Map();
      res.list
         .filter((sensor) => sensor.sensor_type && sensor.sensor_type.trim() !== '' && sensor.sensor_name && sensor.sensor_name.trim() !== '')
         .map((sensor, index, array) => {
           const newItem: TemperatureSensor = {
             id:                 null,
             sensor_type_id:     sensor.id,
             vehicle_id:         vehicle_id,
             user_serial_number: array.length - index,
           }

           return map.set(
             index,
             {
               ...newItem,
               __oldValue: null,
               __errors:   [],
             },
           );
         })

      setItemsMap(map);
      setRowsToUpdateOrCreate(map);
      setUserSerialNumberDropdownOptions(calculateUserSerialNumberDropDownOptions(map));
      setSensorTypeDropdownOptions(calculateSensorTypeDropDownOptions(map));
    } catch (e) {
      console.log(e);
    } finally {
      setIsRequestPending(false);
    }
  }

  return (
    <>
      {
        <div onClick={() => setIsOpen(true)}>Настроить</div>
      }
      <Modal
        isOpen={isOpen}
        onDismiss={() => {
          const newMap = new Map();
          setIsOpen(false);
          setIsOpen(false);
          setItemsMap(newMap);
          setUserSerialNumberDropdownOptions(calculateUserSerialNumberDropDownOptions(newMap));
          setSensorTypeDropdownOptions(calculateSensorTypeDropDownOptions(newMap));
        }}
        isBlocking={isRequestPending}
      >
        <ModalWrapper>
          <HeaderWrapper>
            <HeaderTitle
              style={{
                flexGrow:      1,
                textAlign:     'center',
                marginTop:     4,
                marginBottom:  0,
                verticalAlign: 'center',
              }}
            >
              Датчики температуры
            </HeaderTitle>
            <IconButton
              iconProps={{ iconName: 'ChromeClose' }}
              style={{
                flexGrow: 0,
                color:    theme.colors.white,
              }}
              onClick={() => {
                const newMap = new Map();
                setIsOpen(false);
                setIsOpen(false);
                setItemsMap(newMap);
                setUserSerialNumberDropdownOptions(calculateUserSerialNumberDropDownOptions(newMap));
                setSensorTypeDropdownOptions(calculateSensorTypeDropDownOptions(newMap));
              }}
            />
          </HeaderWrapper>
          <DetailsListWrapper>
            <ShimmeredDetailsList
              enableShimmer={isRequestPending}
              items={items}
              columns={columns}
              onRenderItemColumn={(item, index, column) => {
                if (!item || index === undefined || !column) {
                  return;
                }

                switch (column.key) {
                  case 'user_serial_number':
                    return (
                      <TooltipHost
                        content={!isFullCRUD ? 'Недостаточно прав доступа' : ''}
                        id={'add'}
                        calloutProps={{ gapSpace: 0 }}
                        hidden={isFullCRUD}
                      >
                        <Dropdown
                          onChange={(_, option) => {
                            if (option && itemsMap.has(index)) {
                              itemsMap.get(index)!.user_serial_number = option.key as number;
                              rowsToUpdateOrCreate.set(index, item);
                              setUserSerialNumberDropdownOptions(calculateUserSerialNumberDropDownOptions(itemsMap));
                            }
                          }}
                          disabled={!isFullCRUD}
                          options={userSerialNumberDropdownOptions}
                          defaultSelectedKey={item.user_serial_number}
                          errorMessage={
                            itemsMap.get(index)?.['__errors']?.includes('user_serial_number_not_unique')
                            ? 'Значение должно быть уникальным'
                            : itemsMap.get(index)?.['__errors']?.includes('user_serial_number')
                              ? 'Обязательное поле'
                              : ''
                          }
                          aria-labelledby="user_serial_number"
                          calloutProps={{
                            setInitialFocus: false,
                          }}
                        />
                      </TooltipHost>
                    )
                  case 'sensor_type_id':
                    return (
                      <TooltipHost
                        content={!isFullCRUD ? 'Недостаточно прав доступа' : ''}
                        id={'sensor_type_id'}
                        calloutProps={{ gapSpace: 0 }}
                        hidden={isFullCRUD}
                      >
                        <Dropdown
                          onChange={(_, _item) => {
                            if (_item && itemsMap.has(index)) {
                              itemsMap.get(index)!.sensor_type_id = _item.key as number;
                              rowsToUpdateOrCreate.set(index, item);
                              setSensorTypeDropdownOptions(calculateSensorTypeDropDownOptions(itemsMap));
                            }
                          }}
                          disabled={!isFullCRUD}
                          options={sensorTypeDropdownOptions}
                          defaultSelectedKey={item.sensor_type_id}
                          errorMessage={
                            itemsMap.get(index)?.['__errors']?.includes('sensor_type_id_not_unique')
                            ? 'Значение должно быть уникальным'
                            : itemsMap.get(index)?.['__errors']?.includes('sensor_type_id')
                              ? 'Обязательное поле'
                              : ''
                          }
                          aria-labelledby="sensor_type_id"
                        />
                      </TooltipHost>
                    );
                  case 'deleteButton':
                    return (
                      <TooltipHost
                        content={!isFullCRUD ? 'Недостаточно прав доступа' : ''}
                        id={'delete'}
                        calloutProps={{ gapSpace: 0 }}
                        hidden={isFullCRUD}
                        styles={{ root: { display: 'inline-block' } }}
                      >
                        <IconButton
                          style={{ color: theme.colors.themePrimary, backgroundColor: theme.colors.white }}
                          onClick={() => {
                            let newMap: Map<number, IItem> = new Map<number, IItem>(itemsMap);
                            newMap.delete(index);
                            setRowsToDelete((prev) => [...prev, item]);
                            setItemsMap(newMap);
                            setUserSerialNumberDropdownOptions(calculateUserSerialNumberDropDownOptions(newMap));
                            setSensorTypeDropdownOptions(calculateSensorTypeDropDownOptions(newMap));
                          }}
                          iconProps={{ iconName: 'Delete' }}
                          disabled={!isFullCRUD}
                          aria-labelledby="delete"
                        />
                      </TooltipHost>
                    );
                }
              }}
              selectionMode={SelectionMode.none}
              checkboxVisibility={CheckboxVisibility.hidden}
              detailsListStyles={
                {
                  root: {
                    selectors: {
                      '.ms-DetailsHeader':                                {
                        lineHeight:   16,
                        borderBottom: 'none',
                      },
                      '.ms-DetailsHeader-cell':                           {
                        whiteSpace: 'normal',
                        height:     '100%',
                        textAlign:  'center',
                      },
                      '.ms-DetailsHeader-cell:hover':                     {
                        backgroundColor: 'transparent',
                      },
                      '.ms-DetailsRow':                                   {
                        borderBottom:    'none',
                        backgroundColor: 'transparent!important',
                      },
                      '.ms-DetailsHeader-cellTitle':                      {
                        justifyContent: 'space-around',
                        verticalAlign:  'bottom',
                      },
                      '.ms-DetailsRow-cell[data-automation-key="index"]': {
                        display:        'flex',
                        justifyContent: 'space-around',
                      },
                    },
                  },
                }
              }
            />
            {
              itemsMap.size < 8 &&
              <IconButton
                onClick={() => {
                  let index = itemsMap.size;
                  let newMap: Map<number, IItem> = new Map<number, IItem>(itemsMap);
                  const newItem = {
                    id:                 null,
                    user_serial_number: null,
                    sensor_type_id:     null,
                    __oldValue:         null,
                    __errors:           [],
                    vehicle_id,
                  };
                  newMap.set(index, newItem);
                  setItemsMap(newMap);
                  rowsToUpdateOrCreate.set(index, newItem);
                }}
                iconProps={{ iconName: 'Add' }}
                style={{
                  backgroundColor: isFullCRUD ? theme.colors.blue : undefined,
                  color:           theme.colors.white,
                  marginLeft:      35,
                  display:         'block',
                }}
                disabled={!isFullCRUD}
                aria-labelledby="add"
              />
            }
          </DetailsListWrapper>
          <PrimaryButton
            disabled={isRequestPending}
            style={{ marginLeft: 10, backgroundColor: theme.colors.blue }}
            onClick={fillInSensorsByDefault}
          >
            Заполнить по умолчанию
          </PrimaryButton>
          <Stack
            horizontal
            tokens={{ childrenGap: 10 }}
            styles={{ root: { padding: '10px;' } }}
            horizontalAlign="space-between"
          >
            <TooltipHost
              content={!isFullCRUD ? 'Недостаточно прав доступа' : ''}
              id={'applyButton'}
              calloutProps={{ gapSpace: 0 }}
              hidden={isFullCRUD}
            >
              <PrimaryButton
                style={{ backgroundColor: theme.colors.blue }}
                text="Применить"
                disabled={isRequestPending || !isFullCRUD}
                onClick={() => {
                  const user_serial_numberMap = new Map();
                  const sensor_type_idMap = new Map();
                  let hasErrors = false;

                  itemsMap.forEach((item) => {
                    const errors = item['__errors'];

                    if (!item.user_serial_number) {
                      errors.push('user_serial_number');
                      hasErrors = true;
                    } else if (item.user_serial_number && user_serial_numberMap.has(item.user_serial_number)) {
                      errors.push('user_serial_number_not_unique');
                      hasErrors = true;
                    } else {
                      user_serial_numberMap.set(item.user_serial_number, true);
                    }

                    if (!item.sensor_type_id) {
                      errors.push('sensor_type_id');
                      hasErrors = true;
                    } else if (item.sensor_type_id && sensor_type_idMap.has(item.sensor_type_id)) {
                      errors.push('sensor_type_id_not_unique');
                      hasErrors = true;
                    } else {
                      sensor_type_idMap.set(item.sensor_type_id, true);
                    }
                  });

                  if (hasErrors) {
                    console.log(itemsMap)
                    setItemsMap((prev) => new Map(prev));
                  } else {
                    updateTemperatureSensors();
                  }
                }}
                aria-labelledby="applyButton"
              />
            </TooltipHost>
            <DefaultButton
              text="Отмена"
              onClick={() => {
                const newMap = new Map();
                setIsOpen(false);
                setItemsMap(newMap);
                setUserSerialNumberDropdownOptions(calculateUserSerialNumberDropDownOptions(newMap));
                setSensorTypeDropdownOptions(calculateSensorTypeDropDownOptions(newMap));
              }}
              disabled={isRequestPending}
            />
          </Stack>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default SensorSelection;